import { useEffect, useMemo, useRef, useState } from "react";
import { writeImproveHandoff } from "@/hooks/multi-agent";
import type { ReactNode } from "react";
import {
  Activity,
  AlertTriangle,
  Bug,
  Copy,
  Database,
  Download,
  CheckCircle2,
  HelpCircle,
  ListTree,
  MapPin,
  Play,
  RefreshCw,
  Sparkles,
  Square,
  XCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TermWindow } from "@/components/organisms/studio/TermWindow";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { PageHeader } from "@/components/molecules/PageHeader";
import { useMutation } from "@apollo/client/react";
import { REFRESH_SALESFORCE_TOKEN } from "@/gql/auth";
import toast from "react-hot-toast";
import {
  cancelRun,
  createAnalyzeRun,
  createDebugSnapshot,
  getDebugLogBody,
  getRun,
  health,
  listDebugLogs,
  listTracedUsers,
  renewTraceFlag,
  snapshotFileUrl,
  streamRun,
  type DebugAnalysis,
  type DebugEvent,
  type DebugLogBody,
  type DebugLogRow,
  type DebugRootCause,
  type DebugRunSnapshot,
  type DebugSnapshot,
  type DebugParseHealth,
  type SalesforceOrgUser,
} from "@/services/debugging";
import {
  analysisParseHealth,
  analysisVerdict,
  parseHealthLabel,
  parseHealthReasons,
  verdictPresentation,
} from "@/lib/debugging/verdict";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { WeaveIcon } from "@/components/atoms/WeaveIcon";

type Tab = "overview" | "limits" | "runtime" | "methods" | "queries" | "issues" | "flow" | "raw";

const tabs: Array<{ id: Tab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "limits", label: "Limits" },
  { id: "runtime", label: "Runtime" },
  { id: "methods", label: "Methods" },
  { id: "queries", label: "SOQL / DML" },
  { id: "issues", label: "Errors / warnings" },
  { id: "flow", label: "Call flow" },
  { id: "raw", label: "Raw" },
];

export function DebuggingPage() {
  const navigate = useNavigate();
  const selectedOrg = useAuthStoreV1((s) => s.selectedOrg);
  const user = useAuthStoreV1((s) => s.user);
  const [serviceOk, setServiceOk] = useState<boolean | null>(null);
  const [logs, setLogs] = useState<DebugLogRow[]>([]);
  const [selectedLogIds, setSelectedLogIds] = useState<string[]>([]);
  const [activeLog, setActiveLog] = useState<DebugLogRow | null>(null);
  const [rawLog, setRawLog] = useState<DebugLogBody | null>(null);
  const [loadingRawLog, setLoadingRawLog] = useState(false);
  const [rawSearch, setRawSearch] = useState("");
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [includeBodies, setIncludeBodies] = useState(false);
  const [run, setRun] = useState<DebugRunSnapshot | null>(null);
  const [snapshot, setSnapshot] = useState<DebugSnapshot | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [events, setEvents] = useState<DebugEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  // SF reconnect + retry: the debugging service throws a reauth message when the
  // Salesforce token expires — surface a "Reconnect SF" pill (re-mint via the shared
  // mutation) + a Retry that re-runs the last action (mirrors the multi-agent pill).
  const lastAction = useRef<(() => void) | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [refreshSf] = useMutation(REFRESH_SALESFORCE_TOKEN);
  const isReauth = !!error && (error.toLowerCase().includes("reconnect") || error.toLowerCase().includes("expired"));
  const reconnectSf = async () => {
    if (reconnecting) return;
    setReconnecting(true);
    try {
      await refreshSf();
      setError(null);
      toast.success("Salesforce reconnected");
      lastAction.current?.();
    } catch {
      toast.error("Reconnect failed — please log back in.");
    } finally {
      setReconnecting(false);
    }
  };
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const orgLabel =
    selectedOrg?.orgName || selectedOrg?.salesforceOrgId || selectedOrg?.id || "Select an org";
  const analysis = run?.result || null;
  const showAnalysisDashboard = Boolean(run || analysis || events.length);

  useEffect(() => {
    let alive = true;
    setServiceOk(null);
    health()
      .then(() => {
        if (alive) setServiceOk(true);
      })
      .catch(() => {
        if (alive) setServiceOk(false);
      });
    return () => {
      alive = false;
    };
  }, [selectedOrg?.id, user?.id]);

  useEffect(() => {
    if (!selectedOrg || !user || serviceOk !== true) return;
    void loadLogs();
    // Reload when the scoped org/user changes; loadLogs is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg?.id, user?.id, serviceOk]);

  useEffect(() => {
    return () => unsubscribeRef.current?.();
  }, []);

  const startStream = (runId: string) => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = streamRun({
      runId,
      onEvent: (event) => {
        setEvents((prev) => [...prev.slice(-180), event]);
        if (event.status || event.stage || event.progress !== undefined || event.result) {
          setRun((prev) => ({ ...(prev || {}), ...(event as Partial<DebugRunSnapshot>) } as DebugRunSnapshot));
        }
      },
      onError: (err) => setError(err.message),
      onClose: () => {
        void getRun(runId).then(setRun).catch((err) => setError(err.message));
      },
    });
  };

  const loadLogs = async () => {
    lastAction.current = loadLogs;
    setError(null);
    setLoadingLogs(true);
    try {
      const response = await listDebugLogs({ limit: 25 });
      setLogs(response.logs || []);
      setSelectedLogIds([]);
      setActiveLog(null);
      setRawLog(null);
      setRawSearch("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingLogs(false);
    }
  };

  const openRawLog = async (log: DebugLogRow) => {
    setActiveLog(log);
    setError(null);
    setLoadingRawLog(true);
    try {
      const body = await getDebugLogBody(log.Id);
      setRawLog(body);
    } catch (err) {
      setRawLog(null);
      setError((err as Error).message);
    } finally {
      setLoadingRawLog(false);
    }
  };

  const runAnalyze = async () => {
    lastAction.current = runAnalyze;
    setError(null);
    setEvents([]);
    try {
      const created = await createAnalyzeRun({
        limit: selectedLogIds.length ? Math.min(50, Math.max(1, logs.length || selectedLogIds.length)) : 10,
        logIds: selectedLogIds,
        includeBodies,
      });
      setRun(created);
      setSnapshot(null);
      setActiveTab("overview");
      startStream(created.run_id);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const backToLogs = () => {
    unsubscribeRef.current?.();
    setRun(null);
    setEvents([]);
    setSnapshot(null);
    setActiveTab("overview");
  };

  const createSnapshot = async () => {
    if (!run?.run_id || run.status !== "done") return;
    setError(null);
    setSnapshotLoading(true);
    try {
      setSnapshot(await createDebugSnapshot(run.run_id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSnapshotLoading(false);
    }
  };

  // Hand the LLM root-cause + proposed patch to the multi-agent to actually apply
  // the fix — same handoff contract the migration/agent "Improve" buttons use.
  const fixInMultiAgent = (rootCause: DebugRootCause) => {
    const where = rootCause.where || {};
    const fix = rootCause.fix || {};
    const lines: string[] = ["# Salesforce runtime bug — fix request", ""];
    if (rootCause.headline) lines.push(`**Root cause:** ${rootCause.headline}`);
    if (rootCause.what_broke) lines.push(`\n**What broke:** ${rootCause.what_broke}`);
    if (rootCause.why) lines.push(`\n**Why:** ${rootCause.why}`);
    if (where.component) lines.push(`\n**Where:** ${where.component}${where.line ? `:${where.line}` : ""}`);
    if (where.snippet) lines.push("\n```apex\n" + where.snippet + "\n```");
    if (fix.summary) lines.push(`\n**Proposed fix:** ${fix.summary}`);
    if (fix.patch) lines.push("\n```" + (fix.language || "apex") + "\n" + fix.patch + "\n```");
    if (Array.isArray(fix.steps) && fix.steps.length) {
      lines.push("\n**Steps:**");
      fix.steps.forEach((step, i) => lines.push(`${i + 1}. ${step}`));
    }
    if (Array.isArray(rootCause.governor_remediation) && rootCause.governor_remediation.length) {
      lines.push("\n**Governor remediation:**");
      rootCause.governor_remediation.forEach((item) => lines.push(`- ${item}`));
    }
    lines.push("\n---\nApply this fix. Fetch the authoritative class source first, then produce an apply-ready patch and validate it before deploying.");
    const report = lines.join("\n");
    writeImproveHandoff({
      source: "debugger",
      agentName: "Salesforce Debugger",
      report,
      artifacts: (where.snippet || fix.patch)
        ? [{ name: `${where.component || "fix"}.md`, content: `${where.snippet || ""}\n\n${fix.patch || ""}`.slice(0, 15000) }]
        : [],
      runId: run?.run_id || "",
    });
    navigate("/weave?seed=agent-result");
  };

  const selectRecommendedLogs = () => {
    const selected = [...logs]
      .sort((a, b) => scoreLog(b) - scoreLog(a))
      .slice(0, 5)
      .map((log) => log.Id)
      .filter(Boolean);
    setSelectedLogIds(selected);
  };

  const stop = async () => {
    if (!run) return;
    setError(null);
    try {
      await cancelRun(run.run_id);
      const next = await getRun(run.run_id);
      setRun(next);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 bg-background">
      <PageHeader
        icon={Bug}
        title="Salesforce Debugger"
        description="Inspect governor usage, SOQL/DML pressure, call flow, failing components, and fix evidence"
        actions={
          <button
            type="button"
            onClick={() => void reconnectSf()}
            disabled={reconnecting}
            className="studio-button studio-button-secondary h-8 gap-1.5"
            title="Re-mint the Salesforce session token"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${reconnecting ? "animate-spin" : ""}`} />
            {reconnecting ? "Reconnecting…" : "Reconnect SF"}
          </button>
        }
      />

      {error ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <span className="min-w-0 flex-1">{error}</span>
          {isReauth ? (
            <button
              type="button"
              onClick={() => void reconnectSf()}
              disabled={reconnecting}
              className="studio-button studio-button-secondary h-8 shrink-0 gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${reconnecting ? "animate-spin" : ""}`} />
              {reconnecting ? "Reconnecting…" : "Reconnect Salesforce"}
            </button>
          ) : null}
          {lastAction.current ? (
            <button
              type="button"
              onClick={() => { setError(null); lastAction.current?.(); }}
              className="studio-button studio-button-secondary h-8 shrink-0 gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      <StepIndicator
        active={snapshot ? 4 : showAnalysisDashboard ? 3 : rawLog ? 2 : 1}
        canInspect={Boolean(rawLog)}
        canAnalyze={Boolean(run)}
        canSnapshot={run?.status === "done"}
        onSelectLogs={backToLogs}
        onInspectRaw={() => {
          if (!showAnalysisDashboard) return;
          backToLogs();
        }}
        onAnalyze={() => {
          if (run) setActiveTab("overview");
        }}
        onSnapshot={createSnapshot}
      />
      <ProgressStrip run={run} events={events} loadingLogs={loadingLogs} logsLoaded={logs.length} />

      {!showAnalysisDashboard ? (
        <DebugLogTableScreen
          orgLabel={orgLabel}
          serviceOk={serviceOk}
          selectedOrgReady={Boolean(selectedOrg && user)}
          logs={logs}
          selectedLogIds={selectedLogIds}
          setSelectedLogIds={setSelectedLogIds}
          loadingLogs={loadingLogs}
          includeBodies={includeBodies}
          setIncludeBodies={setIncludeBodies}
          onLoadLogs={loadLogs}
          onAnalyze={runAnalyze}
          activeLog={activeLog}
          rawLog={rawLog}
          loadingRawLog={loadingRawLog}
          rawSearch={rawSearch}
          setRawSearch={setRawSearch}
          onOpenRawLog={openRawLog}
          onSelectRecommended={selectRecommendedLogs}
        />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 space-y-4 overflow-y-auto">
            <AnalysisActionBar
              run={run}
              selectedCount={selectedLogIds.length}
              snapshot={snapshot}
              snapshotLoading={snapshotLoading}
              onBack={backToLogs}
              onStop={stop}
              onSnapshot={createSnapshot}
            />
            <VerdictCard analysis={analysis} />
            <RootCauseCard
              rootCause={(analysis?.diagnosis as { root_cause?: DebugRootCause } | undefined)?.root_cause}
              running={run?.status === "queued" || run?.status === "running"}
              hasAnalysis={Boolean(analysis)}
              onFix={fixInMultiAgent}
            />
            <RunStatus run={run} />
            <ResultsPanel analysis={analysis} activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>

          <DebugSidePanel analysis={analysis} events={events} />
        </div>
      )}
    </div>
  );
}

/**
 * The analysis verdict, plus WHY it is undetermined when it is.
 *
 * `unknown` and `parse_failed` are not failures and not clean bills of health — they
 * mean the analyzer could not tell. They render in the caution tone with the parse
 * health reasons spelled out, so a user can see that logs went unread rather than
 * concluding their org is fine.
 */
function VerdictCard({ analysis }: { analysis: DebugAnalysis | null | undefined }) {
  const verdict = analysisVerdict(analysis);
  const parseHealth = analysisParseHealth(analysis);
  if (!verdict) return null;
  const { tone, label, detail } = verdictPresentation(verdict);
  const Icon = verdict === "healthy" ? CheckCircle2 : verdict === "failing" ? XCircle : HelpCircle;
  const iconTone =
    tone === "success"
      ? "text-success"
      : tone === "destructive"
        ? "text-destructive"
        : "text-[hsl(var(--accent-warm))]";
  return (
    <div className="rounded-md border border-border bg-card/60 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className={`h-4 w-4 shrink-0 ${iconTone}`} />
        <span className="text-sm font-medium">Analysis verdict</span>
        <Badge variant={tone}>{label}</Badge>
        {parseHealth ? (
          <Badge variant={parseHealth.status === "ok" ? "secondary" : "warm"}>
            {parseHealthLabel(parseHealth.status)}
          </Badge>
        ) : null}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{detail}</p>
      <ParseHealthReasons health={parseHealth} />
    </div>
  );
}

/** The human-readable reasons the analyzer could not read every log. */
function ParseHealthReasons({ health }: { health: DebugParseHealth | null }) {
  const reasons = parseHealthReasons(health);
  if (!health || health.status === "ok" || reasons.length === 0) return null;
  return (
    <div className="mt-2 border-t border-border/60 pt-2">
      <div className="font-mono text-[11px] uppercase text-muted-foreground">
        Why the analysis is incomplete
      </div>
      <ul className="mt-1 space-y-0.5">
        {reasons.map((reason) => (
          <li key={reason} className="flex gap-1.5 text-xs text-muted-foreground">
            <span className="text-[hsl(var(--accent-warm))]">•</span>
            <span>{reason}</span>
          </li>
        ))}
      </ul>
      <div className="mt-1.5 font-mono text-[11px] text-muted-foreground/70">
        {health.logs_analyzed} of {health.logs_requested} log(s) analyzed
        {health.logs_failed ? ` · ${health.logs_failed} failed` : ""}
        {health.logs_missing?.length ? ` · ${health.logs_missing.length} missing` : ""}
      </div>
    </div>
  );
}

function RootCauseCard({
  rootCause,
  running,
  hasAnalysis,
  onFix,
}: {
  rootCause?: DebugRootCause;
  running: boolean;
  hasAnalysis: boolean;
  onFix: (rc: DebugRootCause) => void;
}) {
  if (!rootCause) {
    if (running) {
      return (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 animate-pulse text-primary" /> Generating AI root cause from the evidence…
          </span>
        </div>
      );
    }
    // Analysis finished but the backend returned no root cause — make it visible so
    // it's obvious the AI panel is wired (and why it's empty), instead of rendering nothing.
    if (hasAnalysis) {
      return (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 font-medium">
            <Sparkles className="h-4 w-4 text-muted-foreground" /> AI Root Cause
          </div>
          <div className="mt-1 text-xs">
            No AI diagnosis returned for this run. The debugging service must be running with a
            <span className="mx-1 font-mono">GEMINI_API_KEY</span>set, and the analyzed log must contain a
            failure or governor-limit signal. Evidence tabs below are unaffected.
          </div>
        </div>
      );
    }
    return null;
  }
  const severity = String(rootCause.severity || "info");
  const accent =
    severity === "error"
      ? "border-destructive/40 bg-destructive/5"
      : severity === "warning"
        ? "border-amber-500/40 bg-amber-500/5"
        : "border-primary/40 bg-primary/5";
  const where = rootCause.where || {};
  const fix = rootCause.fix || {};
  return (
    <div className={`rounded-lg border ${accent} p-4`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">AI Root Cause</span>
          <SevBadge severity={severity} />
          <ConfBadge confidence={rootCause.confidence} />
        </div>
        <button type="button" className="studio-button h-8 gap-1.5" onClick={() => onFix(rootCause)}>
          <WeaveIcon className="h-3.5 w-3.5" /> Weave in
        </button>
      </div>

      <div className="text-base font-semibold leading-snug">{rootCause.headline}</div>

      {rootCause.what_broke || rootCause.why ? (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {rootCause.what_broke ? <Field label="What broke" value={rootCause.what_broke} /> : null}
          {rootCause.why ? <Field label="Why" value={rootCause.why} /> : null}
        </div>
      ) : null}

      {where.component || where.snippet ? (
        <div className="mt-3">
          <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" /> Where
            {where.component ? (
              <span className="font-mono text-foreground">
                {where.component}
                {where.line ? `:${where.line}` : ""}
              </span>
            ) : null}
          </div>
          {where.snippet ? <CodeBlock code={where.snippet} lang="apex" /> : null}
        </div>
      ) : null}

      {fix.summary || fix.patch || (fix.steps && fix.steps.length) ? (
        <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
          <div className="mb-1 text-xs font-semibold text-emerald-700">The fix</div>
          {fix.summary ? <div className="text-sm leading-relaxed">{fix.summary}</div> : null}
          {fix.patch ? <CodeBlock code={fix.patch} lang={fix.language || "apex"} copyable /> : null}
          {Array.isArray(fix.steps) && fix.steps.length ? (
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
              {fix.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}

      {Array.isArray(rootCause.governor_remediation) && rootCause.governor_remediation.length ? (
        <div className="mt-3">
          <div className="mb-1 text-xs font-semibold text-muted-foreground">Governor remediation</div>
          <ul className="list-disc space-y-1 pl-5 text-xs">
            {rootCause.governor_remediation.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {rootCause.generated_by ? (
        <div className="mt-3 text-[11px] text-muted-foreground">
          Generated by {rootCause.generated_by} · grounded in this run&apos;s evidence
        </div>
      ) : null}
    </div>
  );
}

function SevBadge({ severity }: { severity?: string }) {
  const tone =
    severity === "error"
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : severity === "warning"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700"
        : "border-primary/40 bg-primary/10 text-primary";
  return <span className={`rounded border px-2 py-0.5 text-[10px] font-semibold uppercase ${tone}`}>{severity || "info"}</span>;
}

function ConfBadge({ confidence }: { confidence?: string }) {
  if (!confidence) return null;
  return (
    <span className="rounded border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
      confidence: {confidence}
    </span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background/60 p-3">
      <div className="mb-1 text-xs font-semibold text-muted-foreground">{label}</div>
      <div className="text-sm leading-relaxed">{value}</div>
    </div>
  );
}

function CodeBlock({ code, lang, copyable }: { code: string; lang?: string; copyable?: boolean }) {
  const copy = () => {
    void navigator.clipboard?.writeText(code).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed"),
    );
  };
  return (
    <div className="relative mt-2">
      {copyable ? (
        <button
          type="button"
          onClick={copy}
          className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
      ) : null}
      <pre className="overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
        <code>{code}</code>
      </pre>
      {lang ? <div className="mt-1 text-[10px] uppercase text-muted-foreground">{lang}</div> : null}
    </div>
  );
}

function ProgressStrip({
  run,
  events,
  loadingLogs,
  logsLoaded,
}: {
  run: DebugRunSnapshot | null;
  events: DebugEvent[];
  loadingLogs: boolean;
  logsLoaded: number;
}) {
  const latest = events[events.length - 1];
  const progress = Math.max(0, Math.min(100, Number(run?.progress ?? latest?.progress ?? (loadingLogs ? 20 : logsLoaded ? 100 : 0))));
  const status = run?.status || (loadingLogs ? "loading logs" : logsLoaded ? "logs loaded" : "waiting");
  const stage = run?.stage || latest?.stage || (loadingLogs ? "Fetching latest Salesforce debug logs" : logsLoaded ? `${logsLoaded} logs ready for selection` : "Waiting for org and backend");
  const active = loadingLogs || run?.status === "queued" || run?.status === "running";
  return (
    <div className="rounded-md border border-border bg-card px-4 py-3">
      <div className="mb-2 flex items-center justify-between gap-3 text-xs">
        <div className="min-w-0 truncate font-medium">
          <span className="text-muted-foreground">Progress</span>
          <span className="mx-2 text-muted-foreground">/</span>
          <span>{stage}</span>
        </div>
        <div className="shrink-0 font-mono text-muted-foreground">{status} · {progress.toFixed(0)}%</div>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className={`h-full transition-all ${active ? "bg-primary" : "bg-emerald-500"}`} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

function StepIndicator({
  active,
  canInspect,
  canAnalyze,
  canSnapshot,
  onSelectLogs,
  onInspectRaw,
  onAnalyze,
  onSnapshot,
}: {
  active: number;
  canInspect: boolean;
  canAnalyze: boolean;
  canSnapshot: boolean;
  onSelectLogs: () => void;
  onInspectRaw: () => void;
  onAnalyze: () => void;
  onSnapshot: () => void;
}) {
  const steps = ["Select logs", "Inspect raw log", "Analyze", "Snapshot"];
  const handlers = [onSelectLogs, onInspectRaw, onAnalyze, onSnapshot];
  const enabled = [true, canInspect || active > 2, canAnalyze, canSnapshot];
  return (
    <div className="flex flex-wrap gap-2">
      {steps.map((step, index) => {
        const number = index + 1;
        const done = number < active;
        const current = number === active;
        const isEnabled = enabled[index];
        return (
          <button
            key={step}
            type="button"
            disabled={!isEnabled}
            onClick={handlers[index]}
            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs transition ${
              current
                ? "border-primary bg-primary/10 text-primary"
                : done
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
                  : "border-border bg-card text-muted-foreground"
            } ${isEnabled ? "cursor-pointer hover:bg-muted/60" : "cursor-not-allowed opacity-50"}`}
          >
            <span className="font-mono">{number}</span>
            <span className="font-medium">{step}</span>
          </button>
        );
      })}
    </div>
  );
}

function DebugLogTableScreen(props: {
  orgLabel: string;
  serviceOk: boolean | null;
  selectedOrgReady: boolean;
  logs: DebugLogRow[];
  selectedLogIds: string[];
  setSelectedLogIds: (ids: string[]) => void;
  loadingLogs: boolean;
  includeBodies: boolean;
  setIncludeBodies: (value: boolean) => void;
  onLoadLogs: () => void;
  onAnalyze: () => void;
  activeLog: DebugLogRow | null;
  rawLog: DebugLogBody | null;
  loadingRawLog: boolean;
  rawSearch: string;
  setRawSearch: (value: string) => void;
  onOpenRawLog: (log: DebugLogRow) => void;
  onSelectRecommended: () => void;
}) {
  const analyzeDisabled = !props.selectedOrgReady || props.serviceOk === false || props.loadingLogs || props.selectedLogIds.length === 0;
  return (
    <TermWindow title="debug.logs" status={props.loadingLogs ? "loading" : `${props.logs.length} loaded`}>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="text-sm font-semibold">Latest Salesforce debug logs</div>
          <div className="mt-1 truncate text-xs text-muted-foreground">{props.orgLabel}</div>
        </div>
        <div className="flex w-full flex-col gap-2 lg:w-auto lg:items-end">
          <div className="flex w-full flex-wrap items-center gap-2 overflow-x-auto lg:w-auto lg:justify-end">
            <TraceUserPicker onRenewed={props.onLoadLogs} disabled={!props.selectedOrgReady || props.serviceOk === false} />
            <label className="flex h-9 shrink-0 items-center gap-2 rounded-md border border-border px-3 text-xs">
              <input
                type="checkbox"
                checked={props.includeBodies}
                onChange={(event) => props.setIncludeBodies(event.target.checked)}
              />
              Raw bodies
            </label>
            <button className="studio-button studio-button-secondary h-9 min-w-[118px] shrink-0 justify-center" disabled={!props.selectedOrgReady || props.serviceOk === false || props.loadingLogs} onClick={props.onLoadLogs}>
              <RefreshCw className="h-4 w-4" /> {props.loadingLogs ? "Loading" : "Reload logs"}
            </button>
            <button className="studio-button studio-button-secondary h-9 min-w-[92px] shrink-0 justify-center" disabled={!props.logs.length} onClick={() => props.setSelectedLogIds(props.logs.map((log) => log.Id).filter(Boolean))}>
              Select all
            </button>
            <button className="studio-button studio-button-secondary h-9 min-w-[126px] shrink-0 justify-center" disabled={!props.logs.length} onClick={props.onSelectRecommended}>
              Select best logs
            </button>
            <button className="studio-button studio-button-secondary h-9 min-w-[72px] shrink-0 justify-center" disabled={!props.selectedLogIds.length} onClick={() => props.setSelectedLogIds([])}>
              Clear
            </button>
          </div>
          <div className="flex w-full justify-start lg:w-auto lg:justify-end">
            <button className="studio-button h-9 min-w-[190px] shrink-0 justify-center" disabled={analyzeDisabled} onClick={props.onAnalyze}>
              <Play className="h-4 w-4" /> Analyze {props.selectedLogIds.length} selected
            </button>
          </div>
        </div>
      </div>
      <div className="grid min-h-0 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(280px,0.75fr)]">
        <DebugLogsTable
          logs={props.logs}
          selectedLogIds={props.selectedLogIds}
          setSelectedLogIds={props.setSelectedLogIds}
          activeLogId={props.activeLog?.Id || null}
          loading={props.loadingLogs}
          onOpenRawLog={props.onOpenRawLog}
        />
        <RawLogPreview
          activeLog={props.activeLog}
          rawLog={props.rawLog}
          loading={props.loadingRawLog}
          rawSearch={props.rawSearch}
          setRawSearch={props.setRawSearch}
        />
      </div>
    </TermWindow>
  );
}

// Dropdown of users who ALREADY have an active Apex TraceFlag (debug logging on) —
// mirrors the dev-console Traced-Entity list. No org-wide search: only entities being
// traced right now. Renewing extends the selected user's TraceFlag so Salesforce keeps
// generating their logs; it never fabricates logs here — after they reproduce the issue,
// Reload logs pulls the SF-created ApexLogs.
function TraceUserPicker({ onRenewed, disabled }: { onRenewed: () => void; disabled?: boolean }) {
  const [users, setUsers] = useState<SalesforceOrgUser[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [renewing, setRenewing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingUsers(true);
    listTracedUsers()
      .then((list) => {
        if (cancelled) return;
        setUsers(list);
        setSelectedId((prev) => prev || list[0]?.id || "");
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const renew = async () => {
    setRenewing(true);
    try {
      const chosen = users.find((sfUser) => sfUser.id === selectedId) || null;
      await renewTraceFlag({ salesforceUserId: chosen?.id, durationMinutes: 60 });
      toast.success(
        chosen
          ? `Debug logging renewed for ${chosen.name}. Reproduce the issue in Salesforce, then Reload logs.`
          : "Debug logging renewed. Reproduce the issue in Salesforce, then Reload logs."
      );
      onRenewed();
    } catch (err) {
      toast.error((err as Error).message || "Could not renew debug logging");
    } finally {
      setRenewing(false);
    }
  };

  return (
    <div className="flex shrink-0 items-center gap-2">
      <select
        value={selectedId}
        disabled={disabled || loadingUsers}
        onChange={(event) => setSelectedId(event.target.value)}
        title="Users currently being traced (active Apex TraceFlag)"
        className="h-9 w-40 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary disabled:opacity-50 lg:w-56"
      >
        {loadingUsers ? (
          <option value="">Loading traced users…</option>
        ) : users.length ? (
          <>
            <option value="" disabled>
              Trace user…
            </option>
            {users.map((sfUser) => (
              <option key={sfUser.id} value={sfUser.id}>
                {sfUser.name}
                {sfUser.username ? ` · ${sfUser.username}` : ""}
              </option>
            ))}
          </>
        ) : (
          <option value="">No users with active traces</option>
        )}
      </select>
      <button
        type="button"
        className="studio-button studio-button-secondary h-9 min-w-[110px] shrink-0 justify-center"
        disabled={disabled || renewing || !selectedId}
        onClick={renew}
        title="Renew (extend) this user's Apex TraceFlag so Salesforce keeps capturing their debug logs"
      >
        {renewing ? "Renewing…" : "Renew logs"}
      </button>
    </div>
  );
}

function DebugLogsTable({
  logs,
  selectedLogIds,
  setSelectedLogIds,
  activeLogId,
  loading,
  onOpenRawLog,
}: {
  logs: DebugLogRow[];
  selectedLogIds: string[];
  setSelectedLogIds: (ids: string[]) => void;
  activeLogId: string | null;
  loading: boolean;
  onOpenRawLog: (log: DebugLogRow) => void;
}) {
  const toggle = (id: string) => {
    setSelectedLogIds(selectedLogIds.includes(id) ? selectedLogIds.filter((item) => item !== id) : [...selectedLogIds, id]);
  };
  if (!logs.length) {
    return (
      <div className="rounded-md border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
        {loading ? (
          "Loading latest debug logs from Salesforce..."
        ) : (
          <>
            No debug logs found in Salesforce yet.
            <br />
            Pick a <span className="font-medium text-foreground">Trace user</span> above and click{" "}
            <span className="font-medium text-foreground">Renew logs</span> to start capturing — then
            reproduce the issue in Salesforce and hit{" "}
            <span className="font-medium text-foreground">Reload logs</span>.
          </>
        )}
      </div>
    );
  }
  return (
    <div className="overflow-auto rounded-md border border-border">
      <table className="min-w-full table-fixed text-left text-xs">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            <th className="w-10 px-3 py-2" />
            <th className="whitespace-nowrap px-3 py-2 font-medium">Name</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">Signal</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">Status</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">Operation</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">Started</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">Duration</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">Size</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">User</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => {
            const checked = selectedLogIds.includes(log.Id);
            const failed = String(log.Status || "").toLowerCase().includes("fail");
            const active = activeLogId === log.Id;
            return (
              <tr
                key={log.Id}
                className={`cursor-pointer border-t border-border ${active ? "bg-primary/15" : checked ? "bg-primary/10" : "hover:bg-muted/40"}`}
                onClick={() => onOpenRawLog(log)}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggle(log.Id)}
                  />
                </td>
                <td className="max-w-[360px] truncate px-3 py-2 font-medium">{logDisplayName(log)}</td>
                <td className="px-3 py-2"><LogSignalBadges log={log} /></td>
                <td className="px-3 py-2">
                  <span className={`rounded px-2 py-0.5 font-semibold ${failed ? "bg-destructive/15 text-destructive" : "bg-emerald-500/10 text-emerald-700"}`}>
                    {log.Status || "Unknown"}
                  </span>
                </td>
                <td className="max-w-[260px] truncate px-3 py-2 font-mono">{log.Operation || log.Application || "ApexLog"}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono">{formatDateTime(log.StartTime)}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono">{formatMs(log.DurationMilliseconds)}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono">{formatBytes(log.LogLength)}</td>
                <td className="max-w-[220px] truncate px-3 py-2">{log.LogUserName || log.LogUsername || log.LogUserId || ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RawLogPreview({
  activeLog,
  rawLog,
  loading,
  rawSearch,
  setRawSearch,
}: {
  activeLog: DebugLogRow | null;
  rawLog: DebugLogBody | null;
  loading: boolean;
  rawSearch: string;
  setRawSearch: (value: string) => void;
}) {
  const body = rawLog?.body || "";
  const visibleBody = rawSearch.trim() ? filterRawLog(body, rawSearch.trim()) : body;
  const traceEvents = useMemo(() => extractTraceEvents(body), [body]);
  const [selectedTraceIndex, setSelectedTraceIndex] = useState(0);
  const selectedTrace = traceEvents[selectedTraceIndex] || traceEvents[0] || null;
  return (
    <div className="flex flex-1 min-h-0 flex-col rounded-md border border-border bg-background">
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{activeLog ? logDisplayName(activeLog) : "Raw debug log"}</div>
          <div className="mt-1 truncate font-mono text-xs text-muted-foreground">
            {activeLog ? `${activeLog.Id} · ${formatBytes(activeLog.LogLength)}` : "Click any log row to inspect the raw Salesforce log first."}
          </div>
        </div>
        {rawLog ? <span className="shrink-0 rounded border border-border px-2 py-1 font-mono text-xs">{rawLog.line_count} lines</span> : null}
      </div>
      <div className="border-b border-border p-3">
        <input
          className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
          placeholder="Search traces/raw log: USER_DEBUG, FATAL_ERROR, SOQL_EXECUTE, CODE_UNIT_STARTED, class name..."
          value={rawSearch}
          onChange={(event) => setRawSearch(event.target.value)}
        />
      </div>
      <div className="grid min-h-0 flex-1 grid-rows-[minmax(180px,0.45fr)_minmax(220px,0.55fr)]">
        <div className="min-h-0 overflow-auto border-b border-border p-3">
          {loading ? (
            <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Loading trace statements...
            </div>
          ) : traceEvents.length ? (
            <div className="grid min-h-full gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="min-h-0 space-y-2 overflow-auto">
                {traceEvents.map((event, index) => (
                  <button
                    key={`${event.lineNumber}-${index}`}
                    className={`w-full rounded-md border p-3 text-left ${index === selectedTraceIndex ? "border-primary bg-primary/10" : "border-border hover:bg-muted/40"}`}
                    onClick={() => setSelectedTraceIndex(index)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs font-semibold">{event.event}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">L{event.lineNumber}</span>
                    </div>
                    <div className="mt-1 truncate font-mono text-xs text-muted-foreground">{event.summary}</div>
                  </button>
                ))}
              </div>
              <div className="min-h-0 overflow-auto rounded-md border border-border bg-muted/20 p-3">
                {selectedTrace ? (
                  <>
                    <div className="mb-2 flex flex-wrap gap-2 text-xs">
                      <span className="rounded bg-background px-2 py-1 font-semibold">{selectedTrace.event}</span>
                      <span className="rounded bg-background px-2 py-1 font-mono">line {selectedTrace.lineNumber}</span>
                    </div>
                    <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">{selectedTrace.raw}</pre>
                  </>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No important debug statements parsed yet. The full raw log is below.
            </div>
          )}
        </div>
        <div className="min-h-0 overflow-auto p-4">
        {loading ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Loading raw log...
          </div>
        ) : body ? (
          <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-foreground">{visibleBody}</pre>
        ) : (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Select a log row to view the raw log before running analysis.
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function LogSignalBadges({ log }: { log: DebugLogRow }) {
  const badges: Array<{ label: string; tone: string }> = [];
  const size = Number(log.LogLength || 0);
  const duration = Number(log.DurationMilliseconds || 0);
  const operation = String(log.Operation || "");
  const failed = String(log.Status || "").toLowerCase().includes("fail");
  if (failed) badges.push({ label: "Failed", tone: "bg-destructive/15 text-destructive" });
  if (size > 20000) badges.push({ label: "Rich trace", tone: "bg-primary/10 text-primary" });
  if (size > 0 && size < 1000 && operation.includes("/aura")) badges.push({ label: "Tiny shell", tone: "bg-muted text-muted-foreground" });
  if (duration >= 1000) badges.push({ label: "Slow", tone: "bg-amber-500/10 text-amber-700" });
  if (!badges.length) badges.push({ label: "Normal", tone: "bg-emerald-500/10 text-emerald-700" });
  return (
    <div className="flex min-w-[120px] flex-wrap gap-1">
      {badges.slice(0, 2).map((badge) => (
        <span key={badge.label} className={`rounded px-2 py-0.5 text-[11px] font-semibold ${badge.tone}`}>{badge.label}</span>
      ))}
    </div>
  );
}

function AnalysisActionBar({
  run,
  selectedCount,
  snapshot,
  snapshotLoading,
  onBack,
  onStop,
  onSnapshot,
}: {
  run: DebugRunSnapshot | null;
  selectedCount: number;
  snapshot: DebugSnapshot | null;
  snapshotLoading: boolean;
  onBack: () => void;
  onStop: () => void;
  onSnapshot: () => void;
}) {
  const running = run?.status === "queued" || run?.status === "running";
  return (
    <TermWindow title="debugger.actions" status={run?.status || "idle"}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-sm font-semibold">Analyzing {selectedCount} selected log{selectedCount === 1 ? "" : "s"}</div>
          <div className="mt-1 font-mono text-xs text-muted-foreground">{run?.run_id || "No run"}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <button className="studio-button studio-button-secondary h-9 min-w-[104px] shrink-0 justify-center" onClick={onBack}>
            Back to logs
          </button>
          <button className="studio-button studio-button-secondary h-9 min-w-[88px] shrink-0 justify-center" disabled={!running} onClick={onStop}>
            <Square className="h-4 w-4" /> Cancel
          </button>
          <button className="studio-button h-9 min-w-[116px] shrink-0 justify-center" disabled={run?.status !== "done" || snapshotLoading} onClick={onSnapshot}>
            <Download className="h-4 w-4" /> {snapshotLoading ? "Building snapshot" : "Snapshot"}
          </button>
        </div>
      </div>
      {snapshot ? (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
          {snapshot.files.map((file) => (
            <a
              key={file.filename}
              className="studio-button studio-button-secondary justify-center text-xs"
              href={snapshotFileUrl(file.url)}
              target="_blank"
              rel="noreferrer"
            >
              <Download className="h-3.5 w-3.5" /> {file.filename}
            </a>
          ))}
        </div>
      ) : null}
    </TermWindow>
  );
}

function RunStatus({ run }: { run: DebugRunSnapshot | null }) {
  const progress = Math.max(0, Math.min(100, run?.progress ?? 0));
  return (
    <TermWindow title="run.status" status={run?.status || "idle"}>
      <div className="grid gap-4 md:grid-cols-4">
        <Metric icon={<Bug className="h-4 w-4" />} label="Run" value={run?.run_id ? run.run_id.slice(0, 8) : "none"} />
        <Metric icon={<Activity className="h-4 w-4" />} label="Stage" value={run?.stage || "idle"} />
        <Metric icon={<Database className="h-4 w-4" />} label="Kind" value={run?.kind || "none"} />
        <Metric icon={<AlertTriangle className="h-4 w-4" />} label="Error" value={run?.error ? "yes" : "no"} />
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
      </div>
    </TermWindow>
  );
}

function ResultsPanel({
  analysis,
  activeTab,
  setActiveTab,
}: {
  analysis: DebugAnalysis | null;
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
}) {
  return (
    <TermWindow title="analysis" status={analysis ? "complete" : "waiting"}>
      <div className="mb-4 flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`rounded-md border px-3 py-1.5 text-xs font-medium ${activeTab === tab.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {!analysis ? (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Run an analysis to populate runtime evidence.
        </div>
      ) : (
        <ResultTab analysis={analysis} activeTab={activeTab} />
      )}
    </TermWindow>
  );
}

function ResultTab({ analysis, activeTab }: { analysis: DebugAnalysis; activeTab: Tab }) {
  if (activeTab === "overview") {
    return (
      <div className="space-y-4">
        <ObjectTable title="Summary" value={analysis.summary} />
        <ObjectTable title="Transaction debug summary" value={analysis.transaction_debug_summary} />
        <ObjectTable title="Diagnosis" value={analysis.diagnosis} />
        <ObjectTable title="Tool evidence" value={analysis.tool_evidence} />
      </div>
    );
  }
  if (activeTab === "limits") {
    return (
      <div className="space-y-4">
        <SectionTitle title="Combined governor limits" />
        <GovernorLimitTable rows={asRows(analysis.governor_limit_summary)} empty="No governor limit rows parsed across the analyzed logs." />
        <SectionTitle title="Per-log governor evidence" />
        <GovernorLimitTable rows={asRows(analysis.governor_limits)} empty="No per-log governor limit rows parsed." />
      </div>
    );
  }
  if (activeTab === "queries") {
    return (
      <div className="space-y-4">
        <SectionTitle title="SOQL usage" />
        <DataTable rows={asRows(analysis.soql_usage)} empty="No SOQL rows parsed." />
        <SectionTitle title="DML usage" />
        <DataTable rows={asRows(analysis.dml_usage)} empty="No DML rows parsed." />
      </div>
    );
  }
  if (activeTab === "runtime") {
    return (
      <div className="space-y-4">
        <SectionTitle title="Transactions" />
        <DataTable rows={asRows(analysis.transactions)} empty="No transactions parsed." />
        <SectionTitle title="Metadata touched in this process" />
        <DataTable rows={asRows(analysis.runtime_components)} empty="No runtime components parsed." />
      </div>
    );
  }
  if (activeTab === "methods") {
    return (
      <div className="space-y-4">
        <SectionTitle title="Combined method timings" />
        <DataTable rows={asRows(analysis.method_summary)} empty="No method timings parsed." />
        <SectionTitle title="Per-call method evidence" />
        <DataTable rows={asRows(analysis.method_calls)} empty="No method calls parsed." />
      </div>
    );
  }
  if (activeTab === "issues") {
    return (
      <div className="space-y-4">
        <SectionTitle title="Errors and exceptions" />
        <DataTable rows={asRows(analysis.errors_and_exceptions)} empty="No errors or exceptions parsed." />
        <SectionTitle title="Warnings" />
        <DataTable rows={asRows(analysis.warnings)} empty="No warnings parsed." />
        <SectionTitle title="Generated findings" />
        <DataTable rows={asRows(analysis.issues)} empty="No findings generated." />
      </div>
    );
  }
  if (activeTab === "flow") {
    return (
      <div className="space-y-4">
        <SectionTitle title="Call flow" />
        <DataTable rows={asRows(analysis.call_flow)} empty="No call flow parsed." />
      </div>
    );
  }
  return <pre className="max-h-[640px] overflow-auto rounded-md bg-muted p-4 text-xs">{JSON.stringify(analysis, null, 2)}</pre>;
}

function EventRail({ events }: { events: DebugEvent[] }) {
  return (
    <TermWindow title="activity" status={`${events.length} events`}>
      <div className="max-h-[760px] space-y-2 overflow-y-auto">
        {events.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No activity yet.
          </div>
        ) : (
          events.slice().reverse().map((event, index) => (
            <div key={`${event.timestamp}-${index}`} className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs font-semibold">{event.event || event.status || "event"}</span>
                <span className="text-xs text-muted-foreground">{event.progress ?? 0}%</span>
              </div>
              <div className="mt-1 truncate text-xs text-muted-foreground">{event.stage || event.timestamp}</div>
            </div>
          ))
        )}
      </div>
    </TermWindow>
  );
}

/**
 * Header word for the root-cause panel when no errors or warnings were found. An empty
 * error list only means "clean" if the analyzer actually read the logs — with an
 * undetermined verdict it means "we could not tell", which must not read as clean.
 */
function sidePanelCleanliness(analysis: DebugAnalysis | null): string {
  const verdict = analysisVerdict(analysis);
  if (verdict === "unknown") return "undetermined";
  if (verdict === "parse_failed") return "incomplete";
  return "clean";
}

function DebugSidePanel({ analysis, events }: { analysis: DebugAnalysis | null; events: DebugEvent[] }) {
  const limits = asRows(analysis?.governor_limit_summary);
  const summary = analysis?.transaction_debug_summary || {};
  const errors = asRows(analysis?.errors_and_exceptions);
  const warnings = asRows(analysis?.warnings);
  const diagnosis = analysis?.diagnosis as Record<string, unknown> | undefined;
  return (
    <div className="min-w-0 space-y-4 overflow-y-auto">
      <TermWindow title="governor.limits" status={analysis ? "combined" : "waiting"}>
        <div className="space-y-3">
          {limits.length ? limits.slice(0, 8).map((limit, index) => (
            <GaugeRow
              key={`${formatValue(limit.key)}-${index}`}
              label={formatValue(limit.label || limit.key)}
              used={Number(limit.max_used ?? limit.used ?? 0)}
              limit={Number(limit.limit ?? 0)}
              status={formatValue(limit.status)}
            />
          )) : (
            <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
              No governor rows yet.
            </div>
          )}
        </div>
      </TermWindow>

      {/* "clean" is only honest when the analyzer actually managed to read the logs.
          With an undetermined verdict (unknown / parse_failed) an empty error list
          means "we could not tell", so defer to the verdict rather than claiming clean. */}
      <TermWindow
        title="root.cause"
        status={
          errors.length
            ? "errors"
            : warnings.length
              ? "warnings"
              : sidePanelCleanliness(analysis)
        }
      >
        <div className="space-y-3">
          <ObjectTable title="Run" value={summary} />
          {errors.slice(0, 5).map((row, index) => (
            <div key={index} className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <div className="font-semibold text-destructive">{formatValue(row.event || "error")}</div>
              <div className="mt-1 font-mono text-xs">{formatValue(row.component)}</div>
              <div className="mt-2 text-xs text-muted-foreground">{formatValue(row.message)}</div>
            </div>
          ))}
          {!errors.length && warnings.slice(0, 5).map((row, index) => (
            <div key={index} className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
              <div className="font-semibold text-amber-700">{formatValue(row.event || "warning")}</div>
              <div className="mt-1 text-xs text-muted-foreground">{formatValue(row.message)}</div>
            </div>
          ))}
          {!errors.length && !warnings.length && diagnosis ? (
            <ObjectTable title="Diagnosis" value={diagnosis} />
          ) : null}
        </div>
      </TermWindow>

      <EventRail events={events} />
    </div>
  );
}

function GaugeRow({ label, used, limit, status }: { label: string; used: number; limit: number; status: string }) {
  const pct = limit > 0 ? Math.max(0, Math.min(100, (used / limit) * 100)) : 0;
  const color = status === "critical" ? "bg-destructive" : status === "warning" ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3 text-xs">
        <span className="truncate font-medium text-muted-foreground">{label}</span>
        <span className="font-mono">{used} / {limit || "-"}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Metric({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="mt-2 truncate font-mono text-sm">{value}</div>
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return <div className="flex items-center gap-2 text-sm font-semibold"><ListTree className="h-4 w-4" />{title}</div>;
}

function ObjectTable({ title, value }: { title: string; value: unknown }) {
  if (!value || typeof value !== "object") return null;
  return (
    <div>
      <SectionTitle title={title} />
      <DataTable rows={Object.entries(value as Record<string, unknown>).map(([key, val]) => ({ key, value: formatValue(val) }))} empty="No data." />
    </div>
  );
}

function DataTable({ rows, empty }: { rows: Array<Record<string, unknown>>; empty: string }) {
  const columns = useMemo(() => Array.from(new Set(rows.flatMap((row) => Object.keys(row)))).slice(0, 8), [rows]);
  if (!rows.length) {
    return <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">{empty}</div>;
  }
  return (
    <div className="overflow-auto rounded-md border border-border">
      <table className="min-w-full text-left text-xs">
        <thead className="bg-muted text-muted-foreground">
          <tr>{columns.map((col) => <th key={col} className="whitespace-nowrap px-3 py-2 font-medium">{col}</th>)}</tr>
        </thead>
        <tbody>
          {rows.slice(0, 200).map((row, index) => (
            <tr key={index} className="border-t border-border">
              {columns.map((col) => <td key={col} className="max-w-[260px] truncate px-3 py-2 font-mono">{formatValue(row[col])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GovernorLimitTable({ rows, empty }: { rows: Array<Record<string, unknown>>; empty: string }) {
  const columns = [
    "status",
    "namespace",
    "label",
    rows.some((row) => "max_used" in row) ? "max_used" : "used",
    "limit",
    rows.some((row) => "max_percent" in row) ? "max_percent" : "percent",
    "hit_count",
    "logs_hit",
    "log_id",
    "recommendation",
  ].filter((column, index, all) => all.indexOf(column) === index && rows.some((row) => column in row));
  if (!rows.length) {
    return <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">{empty}</div>;
  }
  return (
    <div className="overflow-auto rounded-md border border-border">
      <table className="min-w-full text-left text-xs">
        <thead className="bg-muted text-muted-foreground">
          <tr>{columns.map((col) => <th key={col} className="whitespace-nowrap px-3 py-2 font-medium">{col}</th>)}</tr>
        </thead>
        <tbody>
          {rows.slice(0, 200).map((row, index) => (
            <tr key={index} className="border-t border-border">
              {columns.map((col) => (
                <td key={col} className={`px-3 py-2 font-mono ${col === "recommendation" ? "min-w-[280px] max-w-[520px]" : "max-w-[260px] truncate"}`}>
                  {col === "status" ? <LimitStatus value={formatValue(row[col])} /> : formatValue(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LimitStatus({ value }: { value: string }) {
  const tone =
    value === "critical"
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : value === "warning"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700"
        : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
  return <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{value || "unknown"}</span>;
}

function asRows(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object" && !Array.isArray(item)));
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function logDisplayName(log: DebugLogRow): string {
  if (log.DisplayName) return log.DisplayName;
  const operation = log.Operation || log.Application || "ApexLog";
  const userName = log.LogUserName || log.LogUsername;
  const duration = log.DurationMilliseconds ? `${log.DurationMilliseconds} ms` : null;
  return [operation, userName, duration, log.Status].filter(Boolean).join(" · ");
}

function scoreLog(log: DebugLogRow): number {
  let score = 0;
  const size = Number(log.LogLength || 0);
  const duration = Number(log.DurationMilliseconds || 0);
  const operation = String(log.Operation || "");
  const status = String(log.Status || "").toLowerCase();
  if (status.includes("fail")) score += 1000;
  if (size > 20000) score += 400;
  if (duration >= 1000) score += 250;
  if (operation.includes("/services/data")) score += 120;
  if (operation.includes("/aura") && size < 1000) score -= 250;
  return score + Math.min(size / 1000, 200) + Math.min(duration / 50, 100);
}

function filterRawLog(body: string, term: string): string {
  const needle = term.toLowerCase();
  const lines = body.split("\n");
  const selected = lines.filter((line) => line.toLowerCase().includes(needle));
  if (!selected.length) return `No raw log lines match "${term}".`;
  return selected.slice(0, 1000).join("\n");
}

function extractTraceEvents(body: string): Array<{ lineNumber: number; event: string; summary: string; raw: string }> {
  if (!body) return [];
  const interesting = [
    "USER_DEBUG",
    "FATAL_ERROR",
    "EXCEPTION_THROWN",
    "CODE_UNIT_STARTED",
    "CODE_UNIT_FINISHED",
    "METHOD_ENTRY",
    "METHOD_EXIT",
    "SOQL_EXECUTE_BEGIN",
    "SOQL_EXECUTE_END",
    "DML_BEGIN",
    "DML_END",
    "FLOW_START_INTERVIEW_BEGIN",
    "FLOW_ELEMENT_ERROR",
    "VALIDATION_ERROR",
    "LIMIT_USAGE_FOR_NS",
  ];
  return body
    .split("\n")
    .map((raw, index) => {
      const parts = raw.split("|");
      const event = parts.find((part) => interesting.includes(part)) || "";
      return {
        lineNumber: index + 1,
        event,
        summary: summarizeTraceLine(raw, event),
        raw,
      };
    })
    .filter((row) => row.event)
    .slice(0, 500);
}

function summarizeTraceLine(raw: string, event: string): string {
  const parts = raw.split("|");
  if (event === "USER_DEBUG") return parts.slice(-1)[0] || raw;
  if (event === "SOQL_EXECUTE_BEGIN") return parts.slice(-1)[0] || "SOQL query started";
  if (event === "DML_BEGIN") return parts.slice(3).join(" · ") || "DML started";
  if (event === "LIMIT_USAGE_FOR_NS") return `Governor limits for ${parts[2] || "(default)"}`;
  return parts.slice(-1)[0] || raw;
}

function formatMs(value: unknown): string {
  const number = Number(value || 0);
  return Number.isFinite(number) && number > 0 ? `${number.toFixed(number >= 10 ? 0 : 2)} ms` : "-";
}

function formatBytes(value: unknown): string {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number <= 0) return "-";
  if (number >= 1024 * 1024) return `${(number / 1024 / 1024).toFixed(1)} MB`;
  if (number >= 1024) return `${(number / 1024).toFixed(1)} KB`;
  return `${number} B`;
}

function formatDateTime(value: unknown): string {
  if (!value) return "";
  const date = new Date(String(value));
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleString([], {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default DebuggingPage;
